// ./src/components/generic/AppTable/AppTable.tsx
import React, { useState } from "react";
import {
  Table,
  Checkbox,
  Text,
  Group,
  Center,
  ScrollArea,
  Paper,
} from "@mantine/core";
import { AppButton } from "../AppButton";

import { EmptyState } from "../EmptyState";
import { AppIcon } from "../AppIcon";
import { LoadingDataBlock } from "../LoadingDataBlock";

export interface SortConfig {
  /** Column key to sort by */
  key: string;
  /** Sort direction */
  direction: "asc" | "desc";
}

export interface AppTableColumn<T = any> {
  /** Unique key for the column */
  key: string;
  /** Column header label */
  label: string;
  /** Whether this column is sortable */
  sortable?: boolean;
  /** Fixed width for the column */
  width?: number | string;
  /** Minimum width for responsive behavior */
  minWidth?: number;
  /** Maximum width for the column */
  maxWidth?: number;
  /** Whether this column should take remaining space (flex: 1) */
  flex?: boolean;
  /** Flex grow value (how much of the remaining space to take) */
  flexGrow?: number;
  /** How to render the cell content */
  render?: (value: any, record: T, index: number) => React.ReactNode;
  /** Whether to hide this column on mobile */
  hideOnMobile?: boolean;
  /** Text alignment */
  align?: "left" | "center" | "right";
  /** Vertical alignment */
  verticalAlign?: "top" | "middle" | "bottom";
}

export interface AppTableProps<T = any> {
  /** Array of data to display */
  data: T[];
  /** Column configurations */
  columns: AppTableColumn<T>[];
  /** Loading state */
  loading?: boolean;
  /** Whether more data can be loaded */
  hasMore?: boolean;
  /** Loading more data state */
  loadingMore?: boolean;
  /** Callback to load more data */
  onLoadMore?: (() => void) | undefined;
  /** Current sort configuration */
  sortBy?: SortConfig[];
  /** Callback when sort changes */
  onSortChange?: (sortBy: SortConfig[]) => void;
  /** Whether to enable multi-column sorting */
  multiSort?: boolean;
  /** Selected row keys */
  selectedKeys?: string[];
  /** Callback when selection changes */
  onSelectionChange?: (selectedKeys: string[]) => void;
  /** Function to get unique key for each row */
  getRowKey?: (record: T, index: number) => string;
  /** Function to check if row needs to be highlighetd */
  highlightRow?: (record: T, index: number) => boolean;
  /** Callback when a row is clicked */
  onRowClick?: (record: T) => void;
  /** Whether to enable row selection */
  selectable?: boolean;
  /** Whether to show select all checkbox */
  selectAll?: boolean;
  /** Empty state configuration */
  emptyState?: {
    title?: string;
    description?: string;
    iconName?: string;
    onClick: any;
  };
  /** Additional className for styling */
  className?: string;
  /** Additional styles */
  style?: React.CSSProperties;
  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Whether to show borders */
  withBorder?: boolean;
  /** Whether to stripe rows */
  striped?: boolean;
  /** Whether to highlight rows on hover */
  highlightOnHover?: boolean;
  hideOnNoData?: Boolean;
}

export const AppTable = <T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  sortBy = [],
  onSortChange,
  multiSort = false,
  selectedKeys = [],
  onSelectionChange,
  getRowKey = (record, index) => record.id || index.toString(),
  highlightRow = () => false,
  onRowClick,
  selectable = false,
  selectAll = false,
  emptyState = {
    title: "No data available",
    description: "There are no records to display",
    iconName: "Database",
    onClick: null,
  },
  className,
  style,
  withBorder = true,
  striped = false,
  highlightOnHover = true,
  hideOnNoData,
}: AppTableProps<T>) => {
  const [internalSort, setInternalSort] = useState<SortConfig[]>(sortBy);

  const currentSort = onSortChange ? sortBy : internalSort;

  const handleSort = (columnKey: string) => {
    const newSort = [...currentSort];
    const existingIndex = newSort.findIndex((s) => s.key === columnKey);

    if (existingIndex >= 0) {
      // Column is already sorted
      if (newSort[existingIndex].direction === "asc") {
        newSort[existingIndex].direction = "desc";
      } else {
        // Remove sort for this column
        newSort.splice(existingIndex, 1);
      }
    } else {
      // Add new sort
      if (multiSort) {
        newSort.push({ key: columnKey, direction: "asc" });
      } else {
        newSort.splice(0, newSort.length, { key: columnKey, direction: "asc" });
      }
    }

    if (onSortChange) {
      onSortChange(newSort);
    } else {
      setInternalSort(newSort);
    }
  };

  const getSortDirection = (columnKey: string): "asc" | "desc" | null => {
    const sort = currentSort.find((s) => s.key === columnKey);
    return sort ? sort.direction : null;
  };

  const getSortPriority = (columnKey: string): number | null => {
    const index = currentSort.findIndex((s) => s.key === columnKey);
    return index >= 0 ? index + 1 : null;
  };

  const handleRowSelection = (rowKey: string, checked: boolean) => {
    if (!onSelectionChange) return;

    let newSelection = [...selectedKeys];
    if (checked) {
      if (!newSelection.includes(rowKey)) {
        newSelection.push(rowKey);
      }
    } else {
      newSelection = newSelection.filter((key) => key !== rowKey);
    }

    onSelectionChange(newSelection);
  };

  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;

    if (checked) {
      const allKeys = data.map((record, index) => getRowKey(record, index));
      onSelectionChange(allKeys);
    } else {
      onSelectionChange([]);
    }
  };

  const handleRowClick = (event: React.MouseEvent, record: T) => {
    if (!onRowClick) return;

    // Don't trigger row click if clicking on interactive elements
    const target = event.target as HTMLElement;
    const isInteractive =
      target.closest("button") ||
      target.closest("a") ||
      target.closest('input[type="checkbox"]') ||
      target.closest("select");

    if (!isInteractive) {
      onRowClick(record);
    }
  };

  const isAllSelected =
    data.length > 0 &&
    data.every((record, index) =>
      selectedKeys.includes(getRowKey(record, index))
    );

  const isIndeterminate = selectedKeys.length > 0 && !isAllSelected;

  // Calculate column styles for header (no vertical align)
  const getHeaderColumnStyle = (column: AppTableColumn<T>) => {
    const style: React.CSSProperties = {
      textAlign: column.align || "left",
      paddingTop: "var(--mantine-spacing-md)",
      paddingBottom: "var(--mantine-spacing-md)",
    };

    if (column.width) {
      // Fixed width
      style.width = column.width;
      style.minWidth = column.minWidth || undefined;
      style.maxWidth = column.maxWidth || undefined;
    } else if (column.flex || column.flexGrow) {
      // Flexible width
      style.minWidth = column.minWidth || 100;
      style.maxWidth = column.maxWidth || undefined;
      style.width = "auto";

      if (column.flexGrow) {
        style.flex = `${column.flexGrow} 1 auto`;
      } else if (column.flex) {
        style.flex = "1 1 auto";
      }
    } else {
      // Auto width with constraints
      style.width = "auto";
      style.minWidth = column.minWidth || undefined;
      style.maxWidth = column.maxWidth || undefined;
    }

    return style;
  };

  // Calculate column styles for body cells (with vertical align)
  const getCellColumnStyle = (column: AppTableColumn<T>) => {
    const style: React.CSSProperties = {
      textAlign: column.align || "left",
      verticalAlign: column.verticalAlign || "middle",
      paddingTop: "var(--mantine-spacing-md)",
      paddingBottom: "var(--mantine-spacing-md)",
    };

    if (column.width) {
      // Fixed width
      style.width = column.width;
      style.minWidth = column.minWidth || undefined;
      style.maxWidth = column.maxWidth || undefined;
    } else if (column.flex || column.flexGrow) {
      // Flexible width
      style.minWidth = column.minWidth || 100;
      style.maxWidth = column.maxWidth || undefined;
      style.width = "auto";

      if (column.flexGrow) {
        style.flex = `${column.flexGrow} 1 auto`;
      } else if (column.flex) {
        style.flex = "1 1 auto";
      }
    } else {
      // Auto width with constraints
      style.width = "auto";
      style.minWidth = column.minWidth || undefined;
      style.maxWidth = column.maxWidth || undefined;
    }

    return style;
  };

  const renderSortIcon = (columnKey: string) => {
    const direction = getSortDirection(columnKey);
    const priority = getSortPriority(columnKey);

    return (
      <Group gap="xs" ml="xs">
        {direction ? (
          <AppIcon
            iconName={direction === "asc" ? "ChevronUp" : "ChevronDown"}
            size={14}
          />
        ) : (
          <AppIcon iconName="Selector" size={14} />
        )}
        {multiSort && priority && currentSort.length > 1 && (
          <Text size="xs" c="dimmed">
            {priority}
          </Text>
        )}
      </Group>
    );
  };

  // Map column align to Group justify prop
  const getHeaderJustify = (
    align?: "left" | "center" | "right"
  ): "flex-start" | "center" | "flex-end" => {
    switch (align) {
      case "right":
        return "flex-end";
      case "center":
        return "center";
      default:
        return "flex-start";
    }
  };

  const renderHeader = () => (
    <Table.Thead>
      <Table.Tr
        style={{
          background: "var(--mantine-primary-color-light)",
          color: "var(--mantine-primary-color-light-color)",
        }}
      >
        {selectable && (
          <Table.Th
            w={50}
            style={{
              paddingTop: "var(--mantine-spacing-md)",
              paddingBottom: "var(--mantine-spacing-md)",
              paddingLeft: "var(--mantine-spacing-md)",
            }}
          >
            {selectAll && (
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={(event) =>
                  handleSelectAll(event.currentTarget.checked)
                }
              />
            )}
          </Table.Th>
        )}

        {columns.map((column) => (
          <Table.Th
            key={column.key}
            style={getHeaderColumnStyle(column)}
            className={column.hideOnMobile ? "mantine-hidden-mobile" : ""}
          >
            {column.sortable ? (
              <Group
                gap="xs"
                wrap="nowrap"
                justify={getHeaderJustify(column.align)}
                style={{ cursor: "pointer" }}
                onClick={() => handleSort(column.key)}
              >
                <Text fw={500}>{column.label}</Text>
                {renderSortIcon(column.key)}
              </Group>
            ) : (
              <Group justify={getHeaderJustify(column.align)} wrap="nowrap">
                <Text fw={500}>{column.label}</Text>
              </Group>
            )}
          </Table.Th>
        ))}
      </Table.Tr>
    </Table.Thead>
  );

  const renderBody = () => (
    <Table.Tbody>
      {data.map((record, index) => {
        const rowKey = getRowKey(record, index);
        const isSelected = selectedKeys.includes(rowKey);
        const isHighlighted = highlightRow(record, index);
        const backgroundColor = isHighlighted ? "green.0" : "";

        return (
          <Table.Tr
            key={rowKey ?? index}
            bg={backgroundColor}
            onClick={(event) => handleRowClick(event, record)}
            style={onRowClick ? { cursor: "pointer" } : undefined}
          >
            {selectable && (
              <Table.Td
                style={{
                  verticalAlign: "top",
                  paddingTop: "var(--mantine-spacing-md)",
                  paddingLeft: "var(--mantine-spacing-md)",
                }}
              >
                <Checkbox
                  checked={isSelected}
                  onChange={(event) =>
                    handleRowSelection(rowKey, event.currentTarget.checked)
                  }
                />
              </Table.Td>
            )}

            {columns.map((column) => (
              <Table.Td
                key={column.key}
                style={getCellColumnStyle(column)}
                className={column.hideOnMobile ? "mantine-hidden-mobile" : ""}
              >
                {column.render
                  ? column.render(record[column.key], record, index)
                  : record[column.key]}
              </Table.Td>
            ))}
          </Table.Tr>
        );
      })}
    </Table.Tbody>
  );

  const renderLoadMore = () => {
    if (!hasMore || loading || !onLoadMore) return null;

    return (
      <Center p="md">
        <AppButton
          variant="secondary"
          onClick={onLoadMore}
          inProgress={loadingMore}
        >
          Load More
        </AppButton>
      </Center>
    );
  };

  if (loading && data.length === 0) {
    return <LoadingDataBlock />;
  }

  if (!loading && data.length === 0) {
    if (hideOnNoData === true) {
      return null;
    }

    return (
      <Paper
        withBorder={withBorder}
        className={className || ""}
        style={style}
        p="md"
      >
        <EmptyState
          title={emptyState.title!}
          description={emptyState.description}
          iconName={emptyState.iconName}
          onAction={emptyState.onClick}
          size="md"
        />
      </Paper>
    );
  }

  return (
    <Paper withBorder={withBorder} className={className || ""} style={style}>
      <ScrollArea>
        <Table
          striped={striped}
          highlightOnHover={highlightOnHover}
          style={{
            //tableLayout: "fixed",
            width: "100%",
          }}
        >
          {renderHeader()}
          {renderBody()}
        </Table>
      </ScrollArea>

      {renderLoadMore()}
    </Paper>
  );
};
